import { useState, useCallback } from 'react';
import { TableColumn } from './useTableData';

export interface TableState {
  sortRules: Array<{ fieldId: string; direction: 'asc' | 'desc' }>;
  fullMatchesFirst: boolean;
  filterConditions: any[];
}

export interface UseTablePersistentStateReturn {
  tableStates: Record<string, TableState>;
  getTableState: (tableId: string) => TableState | undefined;
  handleStateChange: (tableId: string, state: TableState) => void;
  validateAndCleanFilters: (tableId: string, columns: TableColumn[]) => void;
}

const DEFAULT_TABLE_STATE: TableState = {
  sortRules: [],
  fullMatchesFirst: false,
  filterConditions: [],
};

export const useTablePersistentState = (): UseTablePersistentStateReturn => {
  const [tableStates, setTableStates] = useState<Record<string, TableState>>({});

  const getTableState = useCallback((tableId: string): TableState | undefined => {
    return tableStates[tableId];
  }, [tableStates]);

  const handleStateChange = useCallback((tableId: string, state: TableState) => {
    setTableStates(prev => ({
      ...prev,
      [tableId]: state
    }));
  }, []);

  const validateAndCleanFilters = useCallback((tableId: string, columns: TableColumn[]) => {
    const currentState = tableStates[tableId];
    if (!currentState?.filterConditions?.length) return;

    const validColumns = new Set(columns.map(c => c.key));
    const hasInvalidFilters = currentState.filterConditions.some(
      filter => filter.fieldId && !validColumns.has(filter.fieldId)
    );

    if (hasInvalidFilters) {
      setTableStates(prev => ({
        ...prev,
        [tableId]: DEFAULT_TABLE_STATE
      }));
    }
  }, [tableStates]);

  return {
    tableStates,
    getTableState,
    handleStateChange,
    validateAndCleanFilters,
  };
};
